import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { isAtLeast } from '../utils/roles';
import { apiClient } from '../api/client';
import {
  PRIMARY,
  TEXT_DARK,
  TEXT_MUTED,
  BORDER_LIGHT,
  BG_PAGE,
  BG_CARD,
  BG_SURFACE,
  FONT_HEADING,
} from '../components/legacy-ball/table-management/designTokens';

// ── Types ─────────────────────────────────────────────────────

interface ErrorReport {
  id: string;
  bugNumber: number;
  screenSlug: string;
  screenAlias: string | null;
  currentUrl: string;
  reporterId: string;
  reporterName: string;
  reporterEmail: string;
  browserName: string;
  browserVersion: string;
  deviceOs: string;
  description: string;
  stepsToReproduce: string | null;
  severity: 'Low' | 'Medium' | 'High' | 'Critical';
  status: 'Open' | 'In_Progress' | 'Resolved' | 'Closed';
  createdAt: string;
  updatedAt: string;
}

interface ErrorReportsResponse {
  data: ErrorReport[];
  total: number;
  page: number;
  totalPages: number;
}

type StatusFilter = '' | 'Open' | 'In_Progress' | 'Resolved' | 'Closed';
type SeverityFilter = '' | 'Low' | 'Medium' | 'High' | 'Critical';

// ── Constants ─────────────────────────────────────────────────

const SEVERITY_COLORS: Record<string, { bg: string; text: string }> = {
  Low: { bg: '#f3f4f6', text: '#4b5563' },
  Medium: { bg: '#fff7ed', text: '#c2410c' },
  High: { bg: '#fef2f2', text: '#dc2626' },
  Critical: { bg: '#450a0a', text: '#fecaca' },
};

const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  Open: { bg: '#dbeafe', text: '#1d4ed8' },
  In_Progress: { bg: '#fef9c3', text: '#a16207' },
  Resolved: { bg: '#dcfce7', text: '#15803d' },
  Closed: { bg: '#f3f4f6', text: '#4b5563' },
};

const STATUS_OPTIONS: StatusFilter[] = ['', 'Open', 'In_Progress', 'Resolved', 'Closed'];
const SEVERITY_OPTIONS: SeverityFilter[] = ['', 'Low', 'Medium', 'High', 'Critical'];

// ── Styles ────────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  page: {
    padding: '2rem',
    maxWidth: '1200px',
    margin: '0 auto',
    backgroundColor: BG_PAGE,
    minHeight: '100vh',
  },
  title: {
    fontFamily: FONT_HEADING,
    color: TEXT_DARK,
    fontSize: '1.75rem',
    margin: '0 0 0.25rem 0',
    fontWeight: 700,
  },
  subtitle: {
    color: TEXT_MUTED,
    fontSize: '0.9rem',
    marginBottom: '1.5rem',
  },
  accessDenied: {
    padding: '3rem',
    textAlign: 'center' as const,
    color: '#d32f2f',
  },
  filtersRow: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '12px',
    marginBottom: '16px',
    alignItems: 'flex-end',
  },
  filterGroup: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px',
  },
  filterLabel: {
    fontSize: '11px',
    fontWeight: 600,
    color: TEXT_MUTED,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.3px',
  },
  select: {
    padding: '8px 12px',
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '6px',
    fontSize: '13px',
    backgroundColor: BG_CARD,
    minWidth: '140px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    backgroundColor: BG_CARD,
    borderRadius: '8px',
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  th: {
    textAlign: 'left' as const,
    padding: '12px 16px',
    fontSize: '12px',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    color: TEXT_MUTED,
    borderBottom: `2px solid ${PRIMARY}`,
    fontWeight: 600,
  },
  td: {
    padding: '12px 16px',
    fontSize: '14px',
    borderBottom: `1px solid ${BORDER_LIGHT}`,
    color: TEXT_DARK,
    verticalAlign: 'middle' as const,
  },
  row: {
    cursor: 'pointer',
    transition: 'background-color 0.1s',
  },
  rowExpanded: {
    cursor: 'pointer',
    backgroundColor: BG_SURFACE,
  },
  badge: {
    display: 'inline-block',
    padding: '3px 10px',
    borderRadius: '12px',
    fontSize: '11px',
    fontWeight: 600,
  },
  pagination: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '12px',
    marginTop: '16px',
    padding: '12px 0',
  },
  pageBtn: {
    padding: '8px 14px',
    backgroundColor: BG_CARD,
    color: PRIMARY,
    border: `1px solid ${PRIMARY}`,
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  pageBtnDisabled: {
    padding: '8px 14px',
    backgroundColor: '#f5f5f5',
    color: '#999',
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'not-allowed',
  },
  loading: {
    textAlign: 'center' as const,
    padding: '3rem',
    color: TEXT_MUTED,
    fontSize: '14px',
  },
  emptyState: {
    textAlign: 'center' as const,
    padding: '3rem',
    color: TEXT_MUTED,
    fontSize: '14px',
  },
  error: {
    color: '#d32f2f',
    backgroundColor: '#fdecea',
    padding: '10px 16px',
    borderRadius: '8px',
    marginBottom: '16px',
    fontSize: '13px',
    border: '1px solid #f5c6cb',
  },
  detailPanel: {
    backgroundColor: BG_SURFACE,
    padding: '20px 24px',
    borderBottom: `1px solid ${BORDER_LIGHT}`,
  },
  detailGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '12px 24px',
    marginBottom: '16px',
  },
  detailLabel: {
    fontSize: '11px',
    fontWeight: 600,
    color: TEXT_MUTED,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.3px',
    marginBottom: '2px',
  },
  detailValue: {
    fontSize: '13px',
    color: TEXT_DARK,
  },
  detailSection: {
    marginBottom: '16px',
  },
  detailDescription: {
    fontSize: '13px',
    color: TEXT_DARK,
    whiteSpace: 'pre-wrap' as const,
    backgroundColor: BG_CARD,
    padding: '12px',
    borderRadius: '6px',
    border: `1px solid ${BORDER_LIGHT}`,
    marginTop: '4px',
  },
  statusSelect: {
    padding: '6px 10px',
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '6px',
    fontSize: '13px',
    backgroundColor: BG_CARD,
    cursor: 'pointer',
  },
  successMessage: {
    fontSize: '12px',
    color: '#15803d',
    marginTop: '4px',
    fontWeight: 500,
  },
};

// ── Helpers ───────────────────────────────────────────────────

function formatDate(dateStr: string): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatStatusLabel(status: string): string {
  return status.replace('_', ' ');
}

// ── Component ─────────────────────────────────────────────────

export function ErrorReportsPage() {
  const { user } = useAuth();

  // Data state
  const [reports, setReports] = useState<ErrorReport[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Filter state
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('');
  const [severityFilter, setSeverityFilter] = useState<SeverityFilter>('');

  // Expandable detail state
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [statusUpdating, setStatusUpdating] = useState<string | null>(null);
  const [statusSuccess, setStatusSuccess] = useState<string | null>(null);

  const loadReports = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      let path = `/error-reports?page=${page}`;
      if (statusFilter) path += `&status=${statusFilter}`;
      if (severityFilter) path += `&severity=${severityFilter}`;

      const response = await apiClient<ErrorReportsResponse>(path);
      setReports(response.data);
      setTotal(response.total);
      setTotalPages(response.totalPages);
    } catch (err: any) {
      setError(err?.message || 'Failed to load error reports.');
      setReports([]);
      setTotal(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, severityFilter]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [statusFilter, severityFilter]);

  const handleRowClick = (id: string) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const handleStatusChange = async (reportId: string, newStatus: string) => {
    setStatusUpdating(reportId);
    setStatusSuccess(null);
    try {
      await apiClient(`/error-reports/${reportId}`, {
        method: 'PATCH',
        body: { status: newStatus },
      });
      // Update local state
      setReports((prev) =>
        prev.map((r) =>
          r.id === reportId ? { ...r, status: newStatus as ErrorReport['status'] } : r,
        ),
      );
      setStatusSuccess(reportId);
      setTimeout(() => setStatusSuccess(null), 3000);
    } catch (err: any) {
      setError(err?.message || 'Failed to update status.');
    } finally {
      setStatusUpdating(null);
    }
  };

  // Access check
  if (!user || !isAtLeast(user.role, 'Admin')) {
    return (
      <div style={styles.accessDenied}>
        <h2>Access Denied</h2>
        <p>You do not have permission to view bug reports.</p>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <h1 style={styles.title}>Bug Reports</h1>
      <p style={styles.subtitle}>
        {total} report{total !== 1 ? 's' : ''} submitted
      </p>

      {/* Filters */}
      <div style={styles.filtersRow}>
        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>Status</label>
          <select
            style={styles.select}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            aria-label="Filter by status"
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt || 'all'} value={opt}>
                {opt ? formatStatusLabel(opt) : 'All'}
              </option>
            ))}
          </select>
        </div>

        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>Severity</label>
          <select
            style={styles.select}
            value={severityFilter}
            onChange={(e) => setSeverityFilter(e.target.value as SeverityFilter)}
            aria-label="Filter by severity"
          >
            {SEVERITY_OPTIONS.map((opt) => (
              <option key={opt || 'all'} value={opt}>
                {opt || 'All'}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Error */}
      {error && <div style={styles.error}>{error}</div>}

      {/* Loading */}
      {loading && <div style={styles.loading}>Loading bug reports...</div>}

      {/* Table */}
      {!loading && reports.length > 0 && (
        <>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Bug ID</th>
                <th style={styles.th}>Screen</th>
                <th style={styles.th}>Severity</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Reporter</th>
                <th style={styles.th}>Submitted</th>
              </tr>
            </thead>
            <tbody>
              {reports.map((report) => (
                <React.Fragment key={report.id}>
                  <tr
                    style={expandedId === report.id ? styles.rowExpanded : styles.row}
                    onClick={() => handleRowClick(report.id)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleRowClick(report.id);
                      }
                    }}
                    aria-expanded={expandedId === report.id}
                    aria-label={`Bug report from ${report.reporterName}: ${report.screenAlias || report.screenSlug}`}
                  >
                    <td style={{ ...styles.td, fontWeight: 700, fontFamily: 'monospace', fontSize: '0.8rem' }}>
                      BUG-{String(report.bugNumber).padStart(3, '0')}
                    </td>
                    <td style={styles.td}>
                      {report.screenAlias || report.screenSlug}
                    </td>
                    <td style={styles.td}>
                      <span
                        style={{
                          ...styles.badge,
                          backgroundColor: SEVERITY_COLORS[report.severity]?.bg,
                          color: SEVERITY_COLORS[report.severity]?.text,
                        }}
                      >
                        {report.severity}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <span
                        style={{
                          ...styles.badge,
                          backgroundColor: STATUS_COLORS[report.status]?.bg,
                          color: STATUS_COLORS[report.status]?.text,
                        }}
                      >
                        {formatStatusLabel(report.status)}
                      </span>
                    </td>
                    <td style={styles.td}>{report.reporterName}</td>
                    <td style={styles.td}>{formatDate(report.createdAt)}</td>
                  </tr>

                  {/* Expandable detail row */}
                  {expandedId === report.id && (
                    <tr>
                      <td colSpan={5} style={{ padding: 0 }}>
                        <div style={styles.detailPanel}>
                          {/* Environmental data grid */}
                          <div style={styles.detailGrid}>
                            <div>
                              <div style={styles.detailLabel}>Screen Slug</div>
                              <div style={styles.detailValue}>{report.screenSlug}</div>
                            </div>
                            <div>
                              <div style={styles.detailLabel}>Screen Alias</div>
                              <div style={styles.detailValue}>{report.screenAlias || '—'}</div>
                            </div>
                            <div>
                              <div style={styles.detailLabel}>URL</div>
                              <div style={styles.detailValue}>{report.currentUrl}</div>
                            </div>
                            <div>
                              <div style={styles.detailLabel}>Browser</div>
                              <div style={styles.detailValue}>
                                {report.browserName} {report.browserVersion}
                              </div>
                            </div>
                            <div>
                              <div style={styles.detailLabel}>Operating System</div>
                              <div style={styles.detailValue}>{report.deviceOs}</div>
                            </div>
                            <div>
                              <div style={styles.detailLabel}>Reporter</div>
                              <div style={styles.detailValue}>
                                {report.reporterName} ({report.reporterEmail})
                              </div>
                            </div>
                            <div>
                              <div style={styles.detailLabel}>Submitted</div>
                              <div style={styles.detailValue}>{formatDate(report.createdAt)}</div>
                            </div>
                            <div>
                              <div style={styles.detailLabel}>Update Status</div>
                              <div>
                                <select
                                  style={styles.statusSelect}
                                  value={report.status}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    handleStatusChange(report.id, e.target.value);
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                  disabled={statusUpdating === report.id}
                                  aria-label={`Update status for report from ${report.reporterName}`}
                                >
                                  <option value="Open">Open</option>
                                  <option value="In_Progress">In Progress</option>
                                  <option value="Resolved">Resolved</option>
                                  <option value="Closed">Closed</option>
                                </select>
                                {statusSuccess === report.id && (
                                  <div style={styles.successMessage}>✓ Status updated</div>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Description */}
                          <div style={styles.detailSection}>
                            <div style={styles.detailLabel}>Description</div>
                            <div style={styles.detailDescription}>
                              {report.description}
                            </div>
                          </div>

                          {/* Steps to reproduce */}
                          {report.stepsToReproduce && (
                            <div style={styles.detailSection}>
                              <div style={styles.detailLabel}>Steps to Reproduce</div>
                              <div style={styles.detailDescription}>
                                {report.stepsToReproduce}
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          <div style={styles.pagination}>
            <button
              style={page <= 1 ? styles.pageBtnDisabled : styles.pageBtn}
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              aria-label="Previous page"
            >
              ← Previous
            </button>

            <span style={{ fontSize: '13px', color: TEXT_MUTED }}>
              Page {page} of {totalPages}
            </span>

            <button
              style={page >= totalPages ? styles.pageBtnDisabled : styles.pageBtn}
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              aria-label="Next page"
            >
              Next →
            </button>
          </div>
        </>
      )}

      {/* Empty state */}
      {!loading && !error && reports.length === 0 && (
        <div style={styles.emptyState}>
          <p>No bug reports found.</p>
          <p style={{ fontSize: '12px', color: '#999' }}>
            Try adjusting your filters or check back later.
          </p>
        </div>
      )}
    </div>
  );
}
